import type React from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Dropdown, IconButton, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import {
    StyledAccountPageLink,
    StyledDDItem,
    StyledLockDDItem,
} from "app/pages/sales-intelligence/modules/enrichment/containers/EnrichmentTabsContainer/styles";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";
import { openHooksModalV4 } from "app/services/ModalService";

const enrichmentTypes = ["company", "contact"] as const;
const ENRICHMENT_DD_ID = "enrichment-tabs-dd";

type Props = {
    onClick: (item: { id: string }) => void;
    isDisabled: boolean;
};

export const EnrichmentTypeDropdown: React.FC<Props> = ({ onClick, isDisabled }) => {
    const { translate } = useTranslate();

    const { isContactsVisible, isLoadingContactStatus, isContactsDisabledByCountry } = useIsContactsAvailable();
    const isContactsAvailable = isContactsVisible || isLoadingContactStatus;

    const renderDropdownItems = () =>
        enrichmentTypes.map((type) => {
            const isOptionDisabled = type === "contact" && !isContactsAvailable;
            const isDisabledByCountry = type === "contact" && isContactsDisabledByCountry;

            const handleDisabledClick = () => {
                openHooksModalV4("contactsEnrichment");
            };

            if (isDisabledByCountry) {
                return (
                    <Tooltip
                        content={
                            <>
                                {translate("si.enrichment.dd.disabled_by_country")}
                                <br />
                                <StyledAccountPageLink href={solutionUrlsService.getAccountManagementUrl()}>
                                    {translate("si.enrichment.dd.disabled_by_country.link")}
                                </StyledAccountPageLink>
                            </>
                        }
                        placement="left"
                    >
                        <div>
                            <StyledDDItem
                                key={type}
                                id={type}
                                disabled
                            >
                                {translate(`si.enrichment.dd.type.${type}`)}
                            </StyledDDItem>
                        </div>
                    </Tooltip>
                );
            }

            if (isOptionDisabled) {
                return (
                    <StyledLockDDItem
                        key={type}
                        id={type}
                        preventDefault
                        onClick={handleDisabledClick}
                    >
                        {translate(`si.enrichment.dd.type.${type}`)}
                    </StyledLockDDItem>
                );
            }

            return (
                <StyledDDItem
                    key={type}
                    id={type}
                >
                    {translate(`si.enrichment.dd.type.${type}`)}
                </StyledDDItem>
            );
        });

    const handleToggle = (isOpen: boolean) => {
        if (isOpen) {
            enrichmentTrackingService.csv.trackTypeDDToggle();
        }
    };

    return (
        <Box
            id={ENRICHMENT_DD_ID}
            position="relative"
        >
            <Dropdown
                appendTo={`#${ENRICHMENT_DD_ID}`}
                virtualize={false}
                onClick={onClick}
                width={224}
                dropdownPopupPlacement="bottom-right"
                onToggle={handleToggle}
            >
                <IconButton
                    type="primary"
                    iconName="upload-file"
                    isDisabled={isDisabled}
                >
                    {translate("si.section.enrichment.upload-csv.button")}
                </IconButton>
                {...renderDropdownItems()}
            </Dropdown>
        </Box>
    );
};
